import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {string | undefined} */
  #pendingNote;
  /** @type {string | undefined} */
  #draftNote;
  /** @type {'pending' | 'saved' | 'error'} */
  #status = 'saved';
  /** @type {InputEvent | undefined} */
  #latestEvent;
  #saving = false;
  #scheduleSave = debounce(() => this.#save(), 200);

  updatedCallback() {
    super.updatedCallback();
    if (!this.#saving && this.#pendingNote === undefined) return;
    // A product/discount response can contain the note from before this edit.
    // Keep the draft and recovery controls, not that stale section snapshot.
    const input = this.querySelector('textarea[name="note"]');
    if (input instanceof HTMLTextAreaElement && this.#draftNote !== undefined) {
      input.value = this.#draftNote;
    }
    this.#setStatus(this.#status);
  }

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    this.#pendingNote = event.target.value;
    this.#draftNote = event.target.value;
    this.#latestEvent = event;
    this.#setStatus('pending');
    this.#scheduleSave();
  };

  retryCartNote = () => {
    this.#setStatus('pending');
    return this.#save();
  };

  /** @param {'pending' | 'saved' | 'error'} state */
  #setStatus(state) {
    this.#status = state;
    const status = this.querySelector('[data-cart-note-status]');
    const retry = this.querySelector('[data-cart-note-retry]');
    if (status) {
      status.textContent = {
        pending: 'Zapisywanie uwagi…',
        saved: 'Uwaga zapisana.',
        error: 'Nie udało się zapisać uwagi. Twój tekst jest nadal w polu.',
      }[state];
    }
    if (retry instanceof HTMLElement) retry.hidden = state !== 'error';
  }

  async #save() {
    if (this.#saving || this.#pendingNote === undefined) return;
    this.#saving = true;
    try {
      // Cancelling a fetch does not roll back a write at Shopify. Finish each
      // write before sending the newest edit so an older note cannot win a race.
      while (this.#pendingNote !== undefined) {
        const note = this.#pendingNote;
        const event = this.#latestEvent;
        this.#pendingNote = undefined;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(Theme.routes.cart_update_url, {
            ...fetchConfig('json', { body: JSON.stringify({ note }) }),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('note_update_failed');
          const saved = await response.json();
          if (String(saved.note ?? '') !== note) throw new Error('note_not_confirmed');
          if (this.#pendingNote === undefined) this.#setStatus('saved');
        } catch (error) {
          if (this.#pendingNote === undefined) this.#pendingNote = note;
          this.#setStatus('error');
          return;
        } finally {
          clearTimeout(timer);
          if (event) cartPerformance.measureFromEvent('note-update:user-action', event);
        }
      }
    } finally {
      this.#saving = false;
    }
  }
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
